/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';

interface NegativeBalanceRechargeModalProps {
  isOpen: boolean;
  balance: number;
}

// Non-dismissible modal shown whenever the user's wallet balance is negative.
// Pods are auto-stopped server-side at the same threshold (LowBalancePodStopper).
// This modal blocks every page except /wallet so the user is funnelled into
// recharging before doing anything else.
const NegativeBalanceRechargeModal: React.FC<NegativeBalanceRechargeModalProps> = ({
  isOpen,
  balance,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't block the wallet page itself - that's where the user goes to recharge.
  if (!isOpen || location.pathname.startsWith('/billing')) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-gray-900 border border-red-500/30 rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
            </div>
          </div>

          <h3 className="text-xl font-semibold text-white text-center mb-2">
            Wallet Balance Negative
          </h3>

          <p className="text-gray-400 text-center mb-4">
            Your wallet balance has dropped below zero. All running pods have been
            stopped automatically. Please recharge to continue using the platform.
          </p>

          <div className="bg-gray-800 rounded-lg p-4 mb-6">
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Current Balance</span>
              <span className="font-semibold text-red-400">₹{balance.toFixed(2)}</span>
            </div>
          </div>

          <button
            onClick={() => navigate('/billing/payments?action=topup')}
            className="w-full px-4 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors flex items-center justify-center gap-2"
          >
            <WalletIcon className="w-5 h-5" />
            Recharge Wallet
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            Your storage volumes and buckets remain intact and accessible.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NegativeBalanceRechargeModal;
